import type { InitPostHogOptions, InitSentryOptions, LoadMarkerOptions } from '@loop3/observability'

/**
 * The env values every `main.tsx` reads today, gathered by `mountL3App`
 * before calling `resolveMountConfig`. Kept as a plain object (rather than
 * reading `import.meta.env` inside this module) so the option → config
 * mapping stays pure and unit-testable without stubbing Vite's env.
 */
export interface MountL3AppEnv {
  mode: string
  release?: string
  sentryDsn?: string
  posthogKey?: string
  posthogHost?: string
  markerProjectId?: string
  clerkPublishableKey?: string
  clerkSignInUrl?: string
  clerkSignUpUrl?: string
}

export interface ResolvedClerkConfig {
  /** `undefined` when `VITE_CLERK_PUBLISHABLE_KEY` is missing — validate with `assertClerkPublishableKey` before mounting. */
  publishableKey: string | undefined
  signInUrl?: string
  signUpUrl?: string
  allowedRedirectOrigins: string[]
}

export interface ResolvedMountConfig {
  sentry: InitSentryOptions
  posthog: InitPostHogOptions
  marker: LoadMarkerOptions
  clerk: ResolvedClerkConfig
}

interface ResolveMountConfigOptions {
  app: string
  posthog?: { disableAutocapture?: boolean }
  clerk?: { allowedRedirectOrigins?: string[] }
}

/**
 * Maps `MountL3AppOptions` + gathered env into the exact init-config shapes
 * `@loop3/observability`'s `initSentry`/`initPostHog`/`loadMarkerWidget` and
 * `ClerkProvider` expect. Pure and total — never throws, even when the Clerk
 * key is missing, so observability can still initialize before `mountL3App`
 * validates the key (matching the original per-app `main.tsx` ordering).
 */
export function resolveMountConfig(
  options: ResolveMountConfigOptions,
  env: MountL3AppEnv,
): ResolvedMountConfig {
  return {
    sentry: {
      dsn: env.sentryDsn,
      app: options.app,
      environment: env.mode,
      release: env.release,
    },
    posthog: {
      key: env.posthogKey,
      host: env.posthogHost,
      app: options.app,
      environment: env.mode,
      disableAutocapture: options.posthog?.disableAutocapture,
    },
    marker: {
      projectId: env.markerProjectId,
      customData: { app: options.app, environment: env.mode },
    },
    clerk: {
      publishableKey: env.clerkPublishableKey,
      signInUrl: env.clerkSignInUrl,
      signUpUrl: env.clerkSignUpUrl,
      allowedRedirectOrigins: options.clerk?.allowedRedirectOrigins ?? [],
    },
  }
}

/**
 * Validates the Clerk publishable key, matching the `if (!clerkKey) throw`
 * guard every `main.tsx` runs today. Returns the narrowed key so callers get
 * a non-optional value.
 */
export function assertClerkPublishableKey(key: string | undefined): string {
  if (!key) throw new Error('VITE_CLERK_PUBLISHABLE_KEY is required')
  return key
}
